import React, { useMemo } from 'react';
import { FaseCategoriaTorneio } from '../../../services/categoriaTorneioService';
import { FaseGrupoPartidasLite, PartidaLite, EquipeLite } from '../../../services/faseGrupoPartidasService';
import { LinhaClassificacaoFront } from '../../../services/classificacaoService';

interface GrupoPrintSectionProps {
  fase: FaseCategoriaTorneio;
  grupos: FaseGrupoPartidasLite[];
  /**
   * Classificação oficial por grupo (id do grupo → linhas já ordenadas pelo
   * MotorClassificacao do backend, via query classificacaoPorGrupo). A ordem e os
   * números exibidos vêm DAQUI — o componente não recomputa nem reordena no cliente.
   */
  classificacaoPorGrupo?: Record<string, LinhaClassificacaoFront[]>;
}

const GrupoPrintSection: React.FC<GrupoPrintSectionProps> = ({ fase, grupos, classificacaoPorGrupo }) => {
  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    const days = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    const day = days[date.getDay()];
    const dayNum = date.getDate().toString().padStart(2, '0');
    const month = months[date.getMonth()];
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}, ${dayNum}/${month} às ${hours}:${minutes}hs`;
  };

  const formatPlacar = (partida: PartidaLite): string => {
    if (partida.equipeAWO || partida.equipeBWO) {
      return 'W.O.';
    }
    if (partida.placar?.resultadoFinalA !== null && partida.placar?.resultadoFinalA !== undefined &&
        partida.placar?.resultadoFinalB !== null && partida.placar?.resultadoFinalB !== undefined) {
      return `${partida.placar.resultadoFinalA}/${partida.placar.resultadoFinalB}`;
    }
    return '';
  };

  const formatAtletasNames = (equipe?: EquipeLite | null): string => {
    if (!equipe) return '';
    if (equipe.atletas && equipe.atletas.length > 0) {
      return equipe.atletas.map(a => a.usuario?.nome ?? 'Atleta').join(' / ');
    }
    return equipe.nome || '';
  };

  // Lookup de equipe (nome/atletas) por id, derivado das partidas do grupo —
  // usado apenas para EXIBIR jogadores; a classificação/ordem vem do backend.
  const construirEquipesPorId = (grupo: FaseGrupoPartidasLite): Map<string, EquipeLite> => {
    const mapa = new Map<string, EquipeLite>();
    grupo.partidas.forEach(partida => {
      if (partida.equipeA) mapa.set(partida.equipeA.id, partida.equipeA);
      if (partida.equipeB) mapa.set(partida.equipeB.id, partida.equipeB);
    });
    return mapa;
  };

  const sortedGrupos = useMemo(() => {
    return [...grupos].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
  }, [grupos]);

  return (
    <div className="print-fase">
      <div className="print-fase-title">{fase.nome}</div>
      {sortedGrupos.map(grupo => {
        const linhas = classificacaoPorGrupo?.[grupo.id] ?? [];
        const equipesPorId = construirEquipesPorId(grupo);
        const sortedPartidas = [...grupo.partidas].sort((a, b) =>
          (a.numeroPartida ?? 0) - (b.numeroPartida ?? 0)
        );

        return (
          <div key={grupo.id} className="print-grupo">
            <div className="print-grupo-title">{grupo.nome}</div>

            {/* Tabela de Classificação — ordem e números vêm do backend (MotorClassificacao) */}
            {linhas.length > 0 && (
              <table className="print-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>SIT.</th>
                    <th>RANK.</th>
                    <th>JOGADORES</th>
                    <th>JOGOS</th>
                    <th>VITÓRIAS</th>
                    <th>DERROTAS</th>
                    <th>SETS</th>
                    <th>GAMES</th>
                    <th>CLASSIF.</th>
                  </tr>
                </thead>
                <tbody>
                  {linhas.map(linha => {
                    const equipe = equipesPorId.get(linha.equipeId);
                    const atletas = formatAtletasNames(equipe);
                    const jogos = linha.vitorias + linha.derrotas;
                    return (
                      <tr key={linha.equipeId} data-testid={`classificacao-print-row-${linha.equipeId}`}>
                        <td>{linha.posicao}</td>
                        <td>-</td>
                        <td>{linha.posicao}</td>
                        <td style={{ textAlign: 'left' }}>{atletas || equipe?.nome || '-'}</td>
                        <td>{jogos}</td>
                        <td>{linha.vitorias}</td>
                        <td>{linha.derrotas}</td>
                        <td>{linha.setsPro}/{linha.setsContra}</td>
                        <td>{linha.gamesPro}/{linha.gamesContra}</td>
                        <td>-</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {/* Tabela de Jogos */}
            {sortedPartidas.length > 0 && (
              <table className="print-table" style={{ marginTop: '10px' }}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>DATA/HORA</th>
                    <th>JOGADORES 01</th>
                    <th>PLACAR</th>
                    <th>JOGADORES 02</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPartidas.map(partida => (
                    <tr key={partida.id}>
                      <td>{partida.numeroPartida ?? '-'}</td>
                      <td>{partida.data ? formatDateTime(partida.data) : '-'}</td>
                      <td style={{ textAlign: 'left' }}>{formatAtletasNames(partida.equipeA) || 'A DEFINIR'}</td>
                      <td>{formatPlacar(partida)}</td>
                      <td style={{ textAlign: 'left' }}>{formatAtletasNames(partida.equipeB) || 'A DEFINIR'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default GrupoPrintSection;
